// src/documents/exportProcessoEnvase.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import grupoLogo from '../assets/grupo_logo.png';

const PRODUCTS_COLLECTION = 'products';

/**
 * Loads and adds the company logo to the workbook
 * @param {ExcelJS.Workbook} workbook 
 * @returns {Promise<number>} The image ID
 */
const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }
        const blob = await logoResponse.blob();

        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}". Converta o logo para PNG ou JPEG.`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        const logoImageId = workbook.addImage({ base64, extension: ext });

        if (logoImageId === undefined || logoImageId === null) {
            throw new Error(
                'Logo não pôde ser adicionado à planilha. ' +
                'O arquivo pode estar corrompido ou o formato real não corresponde ao MIME detectado.'
            );
        }

        return logoImageId;
    } catch (error) {
        console.error('Logo loading error:', error);
        throw new Error(`Não foi possível carregar o logo: ${error.message}. A geração foi cancelada.`);
    }
};

/**
 * Helper: apply medium border to all cells in a row for specified columns
 */
const applyRowBorder = (worksheet, row, colCount, style = 'medium') => {
    for (let c = 1; c <= colCount; c++) {
        const cell = worksheet.getCell(row, c);
        cell.border = {
            top: { style },
            left: { style: c === 1 ? style : 'thin' },
            bottom: { style },
            right: { style: c === colCount ? style : 'thin' }
        };
    }
};

/**
 * Helper: create a merged title row (dark background, white text)
 */
const addTitleRow = (worksheet, row, text, colCount = 4) => {
    const startCol = 1;
    const endCol = colCount;
    worksheet.mergeCells(row, startCol, row, endCol);
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(row).height = 25;
    applyRowBorder(worksheet, row, colCount);
};

/**
 * Helper: create data row with merged cells and values
 */
const addDataRow = (worksheet, row, cells, rowHeight = 25) => {
    worksheet.getRow(row).height = rowHeight;

    cells.forEach((cellDef, index) => {
        const col = index + 1;
        const cell = worksheet.getCell(row, col);

        if (cellDef.merge) {
            worksheet.mergeCells(row, cellDef.merge.start, row, cellDef.merge.end);
        }

        if (cellDef.value !== undefined) {
            cell.value = cellDef.value;
        }

        if (cellDef.bold) {
            cell.font = { ...cell.font, bold: true, size: 11 };
        } else {
            cell.font = { ...cell.font, size: 11 };
        }

        cell.alignment = {
            horizontal: cellDef.align || 'left',
            vertical: 'middle',
            wrapText: true
        };
    });

    applyRowBorder(worksheet, row, 4);
};

/**
 * Creates a single product worksheet within the workbook
 */
const createProductSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        allRawMaterials,
        allCategories,
        allProductTypes,
        order,
        sheetIndex
    } = params;

    let sheetName = `${item.productCode}_ENVASE_${item.lote || 'SEMLOTE'}`;
    if (sheetName.length > 31) sheetName = sheetName.substring(0, 31);

    const worksheet = workbook.addWorksheet(sheetName);

    // Column widths
    worksheet.getColumn(1).width = 35;
    worksheet.getColumn(2).width = 55;
    worksheet.getColumn(3).width = 20;
    worksheet.getColumn(4).width = 40;

    // Calculations
    const mode = item.mode || 'mass';
    const quantity = parseFloat(item.quantity) || 0;
    const lossIndex = parseFloat(productData.lossIndex) || 1;
    const gramatura = parseFloat(productData.gramatura) || 0;
    const gramaturaUnit = productData.gramaturaUnit || 'g';

    const gramaturaBase = (gramaturaUnit === 'g' || gramaturaUnit === 'ml')
        ? gramatura / 1000
        : gramatura;

    let pesoCalculado, pesoProgramado, perdaPercentual;

    if (mode === 'mass') {
        pesoCalculado = quantity;
        perdaPercentual = ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = quantity * lossIndex;
    } else {
        pesoCalculado = quantity * gramaturaBase;
        perdaPercentual = ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = pesoCalculado * lossIndex;
    }

    let currentRow = 1;

    // ========================================================================
    // ROW 1: Logo + Title + Document Code
    // ========================================================================
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 230, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.mergeCells('B1:C1');
    worksheet.getCell('B1').value = 'PROCESSO DE ENVASE\nQUANTIDADE EFETIVA DE PRODUTO FABRICADO';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('D1').value = 'FQ 35\nRev. 02';
    worksheet.getCell('D1').font = { bold: true, size: 12 };
    worksheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 2;

    // ========================================================================
    // ROW 2: Product Name
    // ========================================================================
    const row2 = worksheet.getRow(2);
    row2.height = 25;
    worksheet.mergeCells('A2:D2');
    worksheet.getCell('A2').value = `Produto: ${item.productCode} - ${item.productName}`;
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 3;

    // ========================================================================
    // ROW 3: LOTE and O.P.
    // ========================================================================
    const row3 = worksheet.getRow(3);
    row3.height = 25;
    worksheet.getCell('A3').value = `LOTE: ${item.lote || '___________'}`;
    worksheet.getCell('A3').font = { bold: true, size: 11 };
    worksheet.getCell('A3').alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells('A3:B3');

    worksheet.getCell('C3').value = `O.P: ${order.opCode || '___________'}`;
    worksheet.getCell('C3').font = { bold: true, size: 11 };
    worksheet.getCell('C3').alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells('C3:D3');

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 4;

    // ========================================================================
    // ROW 4: Data de Fabricação
    // ========================================================================
    const row4 = worksheet.getRow(4);
    row4.height = 25;
    worksheet.mergeCells('A4:D4');
    worksheet.getCell('A4').value = 'DATA DE FABRICAÇÃO: ____/____/____';
    worksheet.getCell('A4').font = { bold: true, size: 11 };
    worksheet.getCell('A4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 5;

    // ========================================================================
    // ROW 5: Data de Validade
    // ========================================================================
    const row5 = worksheet.getRow(5);
    row5.height = 25;
    worksheet.mergeCells('A5:D5');
    worksheet.getCell('A5').value = 'DATA DE VALIDADE: ____/____/____';
    worksheet.getCell('A5').font = { bold: true, size: 11 };
    worksheet.getCell('A5').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 6;

    // ========================================================================
    // ROW 6: INFORMAÇÕES PARA O ENVASE (title)
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'INFORMAÇÕES PARA O ENVASE:');
    currentRow = 7;

    // ========================================================================
    // ROW 7: Quantidade Produzida, Perda, Quantidade Programada
    // ========================================================================
    const row7 = worksheet.getRow(7);
    row7.height = 25;

    worksheet.getCell('A7').value = `QUANTIDADE PRODUZIDA: ${pesoCalculado.toFixed(3)} KG`;
    worksheet.getCell('A7').font = { bold: true, size: 11 };
    worksheet.getCell('A7').alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.getCell('B7').value = `PERDA: ${perdaPercentual}%`;
    worksheet.getCell('B7').font = { bold: true, size: 11 };
    worksheet.getCell('B7').alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells('C7:D7');
    worksheet.getCell('C7').value = `QUANTIDADE PROGRAMADA: ${pesoProgramado.toFixed(3)} KG`;
    worksheet.getCell('C7').font = { bold: true, size: 11 };
    worksheet.getCell('C7').alignment = { horizontal: 'center', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 8;

    // ========================================================================
    // ROW 8: Capacidade do Frasco, Peso do Produto + Tara
    // ========================================================================
    const row8 = worksheet.getRow(8);
    row8.height = 25;

    worksheet.mergeCells('A8:B8');
    const gramaturaDisplay = parseFloat(productData.gramatura) || 0;
    const gramaturaUnitDisplay = productData.gramaturaUnit || 'g';
    worksheet.getCell('A8').value = `CAPACIDADE DO FRASCO: ${gramaturaDisplay} ${gramaturaUnitDisplay.toUpperCase()}`;
    worksheet.getCell('A8').font = { bold: true, size: 11 };
    worksheet.getCell('A8').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C8:D8');
    worksheet.getCell('C8').value = 'PESO DO PRODUTO + TARA: ';
    worksheet.getCell('C8').font = { bold: true, size: 11 };
    worksheet.getCell('C8').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 9;

    // ========================================================================
    // ROW 9: Tanque, Tara da Embalagem
    // ========================================================================
    const row9 = worksheet.getRow(9);
    row9.height = 25;

    worksheet.mergeCells('A9:B9');
    worksheet.getCell('A9').value = 'TANQUE: ';
    worksheet.getCell('A9').font = { bold: true, size: 11 };
    worksheet.getCell('A9').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C9:D9');
    worksheet.getCell('C9').value = 'TARA DA EMBALAGEM: ';
    worksheet.getCell('C9').font = { bold: true, size: 11 };
    worksheet.getCell('C9').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 10;

    // ========================================================================
    // ROW 10: Quantidade Total de Embalagens
    // ========================================================================
    const row10 = worksheet.getRow(10);
    row10.height = 25;
    worksheet.mergeCells('A10:D10');
    let qtdeTotalEmbalagens = '';
    if (gramaturaBase > 0) {
        qtdeTotalEmbalagens = Math.ceil(pesoCalculado / gramaturaBase);
        worksheet.getCell('A10').value = `QUANTIDADE TOTAL DE EMBALAGENS: ${qtdeTotalEmbalagens} UNIDADE(S)`;
    } else {
        worksheet.getCell('A10').value = `QUANTIDADE TOTAL DE EMBALAGENS: __________ UNIDADE(S)`;
    }

    worksheet.getCell('A10').font = { bold: true, size: 11 };
    worksheet.getCell('A10').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 11;

    // ========================================================================
    // ROW 11: Frascos/Acessórios para Embalagem
    // ========================================================================
    const row11 = worksheet.getRow(11);
    row11.height = 25;
    worksheet.mergeCells('A11:D11');

    let packagingDescription = 'FRASCOS/ACESSÓRIOS PARA EMBALAGEM: ';

    // Identify the specific Category and Type IDs needed
    const embalagemCategory = allCategories?.find(
        cat => cat.name?.toUpperCase() === 'EMBALAGENS'
    );
    const serigrafiaType = allProductTypes?.find(
        type => type.name?.toUpperCase() === 'SERIGRAFIA'
    );

    // Filter materials from formula: Category EMBALAGENS, exclude Type SERIGRAFIA and code 'SER'
    const packagingMaterials = (productData.rawMaterials || [])
        .map(mp => {
            const rawMaterial = allRawMaterials.find(rm => rm.id === mp.id);
            return { rawMaterial, quantity: mp.quantity };
        })
        .filter(item => {
            if (!item.rawMaterial) return false;

            const rm = item.rawMaterial;
            const code = (rm.code || "").toUpperCase();

            // Inclusion criteria: Must be in 'EMBALAGENS' category
            const isEmbalagem = embalagemCategory &&
                rm.categoryIds?.includes(embalagemCategory.id);

            if (!isEmbalagem) return false;

            // Exclusion criteria: Type 'SERIGRAFIA' OR code includes 'SER'
            const isSerigrafiaType = serigrafiaType &&
                rm.productTypeIds?.includes(serigrafiaType.id);
            const hasSerCode = code.includes("SER");

            return !isSerigrafiaType && !hasSerCode;
        });

    if (packagingMaterials.length > 0) {
        const packagingList = packagingMaterials.map(item => {
            const rm = item.rawMaterial;
            const code = rm.code || '';
            const name = rm.commercialName || rm.inciName || rm.name || '';
            return `${code} - ${name}`;
        });
        packagingDescription += packagingList.join(' | ');
    } else {
        packagingDescription += '';
    }

    worksheet.getCell('A11').value = packagingDescription;
    worksheet.getCell('A11').font = { bold: true, size: 11 };
    worksheet.getCell('A11').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 12;

    // ========================================================================
    // ROW 12: LIBERAÇÃO DO CONTROLE DE QUALIDADE PARA O ENVASE (title)
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'LIBERAÇÃO DO CONTROLE DE QUALIDADE PARA O ENVASE');
    currentRow = 13;

    // ========================================================================
    // ROW 13: Responsável, Data, Horário
    // ========================================================================
    const row13 = worksheet.getRow(13);
    row13.height = 30;

    worksheet.getCell('A13').value = 'RESPONSÁVEL: ';
    worksheet.getCell('A13').font = { bold: true, size: 11 };
    worksheet.getCell('A13').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.getCell('B13').value = 'DATA: ____/____/____';
    worksheet.getCell('B13').font = { bold: true, size: 11 };
    worksheet.getCell('B13').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C13:D13');
    worksheet.getCell('C13').value = 'HORÁRIO: ____:____';
    worksheet.getCell('C13').font = { bold: true, size: 11 };
    worksheet.getCell('C13').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 14;

    // ========================================================================
    // ROW 14: ENVASE (title)
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'ENVASE');
    currentRow = 15;

    // ========================================================================
    // ROW 15: Envasadora Usada
    // ========================================================================
    const row15 = worksheet.getRow(15);
    row15.height = 25;
    worksheet.mergeCells('A15:D15');
    worksheet.getCell('A15').value = 'ENVASADORA USADA: ';
    worksheet.getCell('A15').font = { bold: true, size: 11 };
    worksheet.getCell('A15').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 16;

    // ========================================================================
    // ROWS 16-20: ENVASE vertical title + details
    // ========================================================================
    const envaseTitleRow = 16;
    const envaseEndRow = 20;

    worksheet.mergeCells(envaseTitleRow, 1, envaseEndRow, 1);
    const envaseVerticalTitle = worksheet.getCell('A16');
    envaseVerticalTitle.value = 'ENVASE';
    envaseVerticalTitle.font = { bold: true, size: 11 };
    envaseVerticalTitle.alignment = { horizontal: 'center', vertical: 'middle', textRotation: 0 };

    worksheet.mergeCells('B16:C16');
    const cell16Left = worksheet.getCell('B16');
    cell16Left.value = 'DATA DE INICIO: ____/____/____';
    cell16Left.font = { size: 11 };
    cell16Left.alignment = { horizontal: 'left', vertical: 'middle' };

    const cell16Right = worksheet.getCell('D16');
    cell16Right.value = 'HORÁRIO DE INICIO: ____:____';
    cell16Right.font = { size: 11 };
    cell16Right.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B17:C17');
    const cell17Left = worksheet.getCell('B17');
    cell17Left.value = 'DATA DE TÉRMINO: ____/____/____';
    cell17Left.font = { size: 11 };
    cell17Left.alignment = { horizontal: 'left', vertical: 'middle' };

    const cell17Right = worksheet.getCell('D17');
    cell17Right.value = 'HORÁRIO DE TÉRMINO: ____:____';
    cell17Right.font = { size: 11 };
    cell17Right.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B18:D18');
    const cell18 = worksheet.getCell('B18');
    cell18.value = 'RESPONSÁVEL PELO ENVASE: ';
    cell18.font = { size: 11 };
    cell18.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B19:D19');
    const cell19 = worksheet.getCell('B19');
    cell19.value = 'AUXILIARES:  ';
    cell19.font = { size: 11 };
    cell19.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B20:D20');
    const cell20 = worksheet.getCell('B20');
    cell20.value = '';
    cell20.font = { size: 11 };

    for (let r = envaseTitleRow; r <= envaseEndRow; r++) {
        applyRowBorder(worksheet, r, 4);
        worksheet.getRow(r).height = 25;
    }
    currentRow = 21;

    // ========================================================================
    // ROWS 21-25: DATAÇÃO vertical title + details
    // ========================================================================
    const datacaoTitleRow = 21;
    const datacaoEndRow = 25;

    worksheet.mergeCells(datacaoTitleRow, 1, datacaoEndRow, 1);
    const datacaoVerticalTitle = worksheet.getCell('A21');
    datacaoVerticalTitle.value = 'DATAÇÃO';
    datacaoVerticalTitle.font = { bold: true, size: 11 };
    datacaoVerticalTitle.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells('B21:D21');
    const cell21 = worksheet.getCell('B21');
    cell21.value = 'RESPONSÁVEL PELA DATAÇÃO: ';
    cell21.font = { size: 11 };
    cell21.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B22:D22');
    const cell22 = worksheet.getCell('B22');
    cell22.value = 'CONFERENTE DA DATAÇÃO: ';
    cell22.font = { size: 11 };
    cell22.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B23:C25');
    const cell23Left = worksheet.getCell('B23');
    cell23Left.value = 'DATAÇÃO INICIAL';
    cell23Left.font = { bold: true, size: 11 };
    cell23Left.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
    cell23Left.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };

    worksheet.mergeCells('D23:D25');
    const cell24Right = worksheet.getCell('D23');
    cell24Right.value = 'DATAÇÃO FINAL';
    cell24Right.font = { bold: true, size: 11 };
    cell24Right.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
    cell24Right.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };

    for (let r = 21; r <= 25; r++) {
        applyRowBorder(worksheet, r, 4);
        worksheet.getRow(r).height = 25;
    }
    currentRow = 26;

    // ========================================================================
    // ROW 26: Quantidade de Embalagens Envasadas
    // ========================================================================
    const row26 = worksheet.getRow(26);
    row26.height = 25;
    worksheet.mergeCells('A26:D26');
    worksheet.getCell('A26').value = 'QUANTIDADE DE EMBALAGENS ENVASADAS: ';
    worksheet.getCell('A26').font = { bold: true, size: 11 };
    worksheet.getCell('A26').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 27;

    // ========================================================================
    // ROW 27: Quantidade para a Quarentena
    // ========================================================================
    const row27 = worksheet.getRow(27);
    row27.height = 25;
    worksheet.mergeCells('A27:D27');
    worksheet.getCell('A27').value = 'QUANTIDADE PARA A QUARENTENA: ';
    worksheet.getCell('A27').font = { bold: true, size: 11 };
    worksheet.getCell('A27').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 28;

    // ========================================================================
    // ROW 28: OBSERVAÇÃO DO PROCESSO DE ENVASE (title)
    // ========================================================================
    addTitleRow(worksheet, currentRow, 'OBSERVAÇÃO DO PROCESSO DE ENVASE');
    currentRow = 29;

    for (let i = 0; i < 4; i++) {
        const row = worksheet.getRow(currentRow);
        row.height = 25;
        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = '';
        worksheet.getCell(`A${currentRow}`).font = { size: 11 };
        applyRowBorder(worksheet, currentRow, 4);
        currentRow++;
    }

    addTitleRow(worksheet, currentRow, 'PERDA FINAL EM (%) DE PRODUTO');
    currentRow = 35;

    const row34 = worksheet.getRow(34);
    row34.height = 25;
    worksheet.mergeCells('A34:D34');
    const cell34 = worksheet.getCell('A34');
    cell34.value = '% __________';
    cell34.font = { size: 11 };
    cell34.alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, 34, 4);
    currentRow = 35;

    const row35 = worksheet.getRow(35);
    row35.height = 30;

    worksheet.mergeCells('A35:B35');
    worksheet.getCell('A35').value = 'VISTO DO CQ: ';
    worksheet.getCell('A35').font = { bold: true, size: 11 };
    worksheet.getCell('A35').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C35:D35');
    worksheet.getCell('C35').value = 'DATA: ____/____/____';
    worksheet.getCell('C35').font = { bold: true, size: 11 };
    worksheet.getCell('C35').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, 35, 4);
    currentRow = 36;

    addTitleRow(worksheet, currentRow, 'INSPEÇÃO FINAL');
    currentRow = 37;

    for (let i = 0; i < 3; i++) {
        const row = worksheet.getRow(currentRow);
        row.height = 25;
        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = '';
        worksheet.getCell(`A${currentRow}`).font = { size: 11 };
        applyRowBorder(worksheet, currentRow, 4);
        currentRow++;
    }

    worksheet.getRow(40).height = 15;
    currentRow = 41;

    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 11/05/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.getCell(`B${currentRow}`).value = 'Revisado por: Magali Murro\nData: 11/05/2026';
    worksheet.getCell(`B${currentRow}`).font = { size: 9 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 11/05/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= 4; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    }

    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

/**
 * Exports the filling/packaging process document for a production order
 */
export const exportProcessoEnvase = async ({
    order,
    allRawMaterials,
    allCategories,
    allProductTypes,
    showNotification
}) => {
    try {
        if (!order || !order.items || order.items.length === 0) {
            showNotification("Ordem sem produtos para gerar o processo de envase.", 'warning');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const logoImageId = await loadLogo(workbook);

        for (let itemIndex = 0; itemIndex < order.items.length; itemIndex++) {
            const item = order.items[itemIndex];

            const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, item.productId));
            if (!productSnap.exists()) continue;

            const productData = productSnap.data();

            createProductSheet(workbook, logoImageId, {
                item,
                productData,
                allRawMaterials,
                allCategories,
                allProductTypes,
                order,
                sheetIndex: itemIndex
            });
        }

        if (workbook.worksheets.length === 0) {
            showNotification("Não foi possível gerar o processo de envase.", 'error');
            return;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `Processo_Envase_${order.opCode || 'OP'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);

        showNotification(`Processo de envase gerado com sucesso para ${workbook.worksheets.length} produto(s)!`, 'success');

    } catch (error) {
        console.error("Erro ao gerar processo de envase:", error);
        showNotification(`Erro ao gerar processo: ${error.message}`, 'error');
    }
};